// THE REAL TRANSPORT — wired, and unreachable until someone sets a variable on purpose.
//
// It is `createV2Client` and nothing else: this file adds no behaviour, because behaviour added here
// would be a second implementation of the contract's rules (version negotiation, the token headers,
// the never-throws discipline) that the generated file already owns.
//
// NOTHING IN THIS FILE NAMES A PROJECT, A URL OR A KEY THAT A REAL SERVER ANSWERS ON, and nothing in this repository
// fills the config in. `HttpConfig` is supplied by the caller at construction; `resolveHttpConfig` reads it from the
// build's environment and returns `null` when it is absent, which is the state this batch still ships in. W4's
// staging batch sets those variables in the Cloudflare Pages project's settings — not here, and not in a committed
// file. `web/.env.example` lists every name with no value behind any of them.
//
// The three suppliers are the seams named in the brief:
//   • `baseUrl`            — one origin, or a function per endpoint;
//   • `getIdToken`         — from `AuthSession` (services/auth), Firebase anonymous auth at launch,
//                            mirroring iOS (WEB_PATHWAY_INVENTORY.md §7);
//   • `getAppCheckToken`   — from `AppCheckTokens`, reCAPTCHA Enterprise on web.
//     Which App Check app ids the server allowlists is D14, Tyler's, and not this batch's. W4.1 built the seam that
//     supplies the last two (`services/firebase.ts`); it refuses, by name, until the config exists.
//
// ── THE ONE EXCEPTION, AND WHY IT IS NOT A GUESS (W4.1) ─────────────────────────────────────────────────────────────
//
// `VITE_V2_BASE_URL=emulator` points the client at the LOCAL FUNCTIONS EMULATOR, on loopback, under the `demo-`
// project id. That is not an origin anyone chose for this client: it is the URL shape the functions emulator prints
// on boot and the one W3's harness has been calling for a whole batch (`functions/test/v2-emulator/harness.ts`:
// `http://127.0.0.1:5001/<project>/<region>/<function>`). A `demo-` project id cannot exist in Firebase, so no
// credential and no billing path reaches it — "this cannot be production" is a fact about the id rather than a rule
// someone has to remember. Every other value of the variable must be an absolute `http(s)` URL that someone typed.

import { createV2Client, type V2Endpoint } from "@contract";
import type { Transport } from "./types";

export interface HttpConfig {
  /** One origin the endpoint name is appended to, or a function returning each endpoint's URL. */
  readonly baseUrl: string | ((endpoint: V2Endpoint) => string);
  /** The signed-in user's Firebase Auth ID token. Anonymous at launch, as on iOS. */
  readonly getIdToken: () => string | Promise<string>;
  /** A Firebase App Check token. */
  readonly getAppCheckToken: () => string | Promise<string>;
}

// THE BASE URL'S STATES AND ITS ONE READER live in `./baseUrl.ts` since B8 (LAUNCH-COPY-B8), so the build's own config
// (`web/deployTargets.ts`, which vite.config.ts loads in Node) reads the variable with the same function this client does;
// this file imports nothing a build config cannot load except the generated client. Re-exported here, unchanged, for every
// caller that has always imported them from this file.
export {
  BASE_URL_VAR, EMULATOR_ORIGIN, EMULATOR_PROJECT, EMULATOR_REGION, EMULATOR_TOKEN, isReachable, readBaseUrl,
  type V2BaseUrl, type V2ReachableBaseUrl,
} from "./baseUrl";
import { EMULATOR_ORIGIN, EMULATOR_PROJECT, EMULATOR_REGION, isReachable, readBaseUrl, BASE_URL_VAR, type V2BaseUrl } from "./baseUrl";

/**
 * THE ONE MAPPING FROM AN ENDPOINT TO ITS URL. Every arm lives here and nowhere else.
 *
 * `null` for the two states that cannot produce one, which is not an oversight to be tidied away later: it is the
 * answer, and the caller's job is to not call a server rather than to invent an address.
 */
export function v2EndpointUrl(base: V2BaseUrl, endpoint: V2Endpoint): string | null {
  switch (base.kind) {
    case "absent":
    case "malformed":
      return null;
    case "origin":
      return `${base.origin}/${endpoint}`;
    case "emulator":
      return `${EMULATOR_ORIGIN}/${EMULATOR_PROJECT}/${EMULATOR_REGION}/${endpoint}`;
  }
}

/** Builds the real transport. The generated client owns every wire concern; this only names the seam. */
export function createHttpTransport(config: HttpConfig): Transport {
  return createV2Client({
    url: config.baseUrl,
    getIdToken: config.getIdToken,
    getAppCheckToken: config.getAppCheckToken,
  });
}

/**
 * The config, or `null` when the build carries none — which is every build this repository produces.
 *
 * A MISSING BASE URL IS NOT A DEFAULT, and neither is a broken one. `readBaseUrl` above is what decides; this only
 * refuses to build a client out of a state that cannot name an address. When you want to know WHY there is no config,
 * call `readBaseUrl` directly and read its `problem` — `main.tsx` does, and says it out loud.
 */
export function resolveHttpConfig(
  env: Record<string, string | undefined>,
  tokens: { getIdToken: () => string | Promise<string>; getAppCheckToken: () => string | Promise<string> },
): HttpConfig | null {
  const base = readBaseUrl(env[BASE_URL_VAR]);
  if (!isReachable(base)) return null;
  return {
    baseUrl: (endpoint: V2Endpoint): string => {
      const url = v2EndpointUrl(base, endpoint);
      // Unreachable: `base` was narrowed to the two states that always produce one. THROWING is still the right
      // answer if it ever is reached — the generated client turns a throwing `url()` into a typed `badRequest`
      // failure, whereas a guessed origin would be the exact defect this file exists to prevent.
      if (url === null) throw new Error(`no v2 base URL for ${endpoint}`);
      return url;
    },
    getIdToken: tokens.getIdToken,
    getAppCheckToken: tokens.getAppCheckToken,
  };
}
